import { useCallback, useMemo } from 'react'
import { useT } from '@transifex/react'
import { TOUR_TYPE, TourStep, TourSteps } from '../../constants'

const FIRST_CASH_PAYMENT_TYPE =
  '#payment-options-group .vd-btn[data-id="1"]:first-child'

export const useSteps = () => {
  const t = useT()

  const steps: TourSteps = useMemo(() => {
    const TRAINING: TourStep[] = [
      {
        id: 'addProduct',
        title: t('Add a product to your sale'),
        text: t(
          'Add a product to your sale, simply search or scan a barcode to add an item to your sale.'
        ),

        attachTo: '.wr-global-search right',
        triggerOn: 'training-sale:complete',
      },
      {
        id: 'addDiscount',
        title: t('Add a discount'),
        text: t(
          'Click the product to discount it. You can also change the quantity and add a note.'
        ),

        attachTo: '.li-cell left',
        triggerOn: 'sale:add-line-item',
      },
      {
        id: 'addCustomer',
        title: t('Record who’s buying'),
        text: t('Search for a customer to link the sale to their name.'),

        attachTo: '.wr-customer-search right',
        triggerOn: 'sale:line-item-price-adjusted:no-customer',
      },
      {
        id: 'takePayment',
        title: t('Take payment'),
        text: t('Click the "Pay" button to finalise the sale.'),

        attachTo: '.sale-action-bar top',
        triggerOn: [
          'search:customer-selected',
          'autocomplete:customer-selected',
          'training-sale:dismiss-payment-screen',
        ],
      },
      {
        id: 'choosePaymentType',
        title: t('Choose a payment type'),
        text: t(`Pick the payment type the customer wishes to use. You can split payments by typing a smaller amount
          into the tender field.`),

        attachTo: '#payment-options-group bottom',
        triggerOn: 'sale:payment',
        delay: 400,
      },
      {
        id: 'exit',
        title: t('Got the hang of it?'),
        text: t(
          'Once you’ve got the hang of this, click here to exit Training Mode.'
        ),
        nextBtn: true,

        attachTo: '.register-info-popover-trigger right',
        triggerOn: [
          'sale:transitioning-to-status',
          'training-sale:force-sale-confirmation-screen',
        ],
        tetherOptions: {
          targetAttachment: 'top left',
          offset: '-20px -120px',
        },
      },
    ]
    const PRIMARY_ONBOARDING: TourStep[] = [
      {
        id: 'searchBar',
        title: t('Search for a product'),
        text: t(
          'The search is designed to be fast and forgiving of spelling mistakes. You can also scan barcodes.'
        ),
        pagination: true,

        attachTo: '.wr-global-search right',
        tetherOptions: {
          attachment: 'top left',
          targetAttachment: 'top right',
          offset: '0px -10px',
        },
      },
      {
        id: 'payment',
        title: t('Click “Pay” to continue the sale'),
        text: t(
          "After adding all items to the checkout, you're ready to make a payment."
        ),
        pagination: true,
        attachTo: '.sale-action-bar top',
        triggerOn: [
          'training-sale:dismiss-payment-screen',
          'sale:add-line-item',
        ],
        trackingData: {
          'sale:add-line-item': {
            step: 'Product Added to Sale',
          },
        },
      },
      {
        id: 'paymentOptions',
        title: t('Select Cash'),
        text: t(
          "Your Lightspeed Retail store can have many payment types. We've set up Cash for you to try."
        ),
        pagination: true,
        attachTo: {
          element: FIRST_CASH_PAYMENT_TYPE,
          on: 'bottom',
        },
        triggerOn: 'sale:payment',
        delay: 600,
        trackingData: {
          'sale:payment': {
            step: 'Clicked Pay Button',
          },
        },
      },
      {
        id: 'exit',
        title: t('Complete Tutorial'),
        text: t(
          'Click here to complete this tutorial and start setting up your store.'
        ),
        pagination: true,

        attachTo: '.sale-confirmation-form-row right',
        triggerOn: [
          'sale:transitioning-to-status',
          'training-sale:force-sale-confirmation-screen',
        ],
        delay: 400,
      },
    ]
    const ONBOARDING: TourStep[] = [
      {
        id: 'searchBar',
        title: t('Search for a product name or scan a barcode'),
        text: t(
          'The search is designed to be fast and forgiving of spelling mistakes. Click the result to add it to the checkout list.'
        ),
        pagination: true,

        attachTo: '.wr-global-search right',
        tetherOptions: {
          attachment: 'top left',
          targetAttachment: 'top right',
          offset: '0px -10px',
        },
      },
      {
        id: 'payment',
        title: t('Click “Pay” to continue the sale'),
        text: t(
          "After adding all items to the checkout, you're ready to make a payment."
        ),
        pagination: true,
        attachTo: '.sale-action-bar top',
        triggerOn: [
          'training-sale:dismiss-payment-screen',
          'sale:add-line-item',
        ],
        trackingData: {
          'sale:add-line-item': {
            step: 'Product Added to Sale',
          },
        },
      },
      {
        id: 'paymentOptions',
        title: t('Select Cash'),
        text: t(
          "Your Lightspeed Retail store can have many payment types. We've set up Cash for you to try."
        ),
        pagination: true,
        attachTo: {
          element: FIRST_CASH_PAYMENT_TYPE,
          on: 'bottom',
        },
        triggerOn: 'sale:payment',
        delay: 600,
        trackingData: {
          'sale:payment': {
            step: 'Clicked Pay Button',
          },
        },
      },
      {
        id: 'exit',
        title: t('Complete Tutorial'),
        text: t(
          'Click here to complete this tutorial and start setting up your store.'
        ),
        pagination: true,

        attachTo: '.sale-confirmation-form-row right',
        triggerOn: [
          'sale:transitioning-to-status',
          'training-sale:force-sale-confirmation-screen',
        ],
        delay: 400,
      },
    ]

    return {
      TRAINING,
      PRIMARY_ONBOARDING,
      ONBOARDING,
    }
  }, [t])

  const getTourSteps = useCallback(
    (tourType: TOUR_TYPE) => {
      if (tourType === TOUR_TYPE.TRAINING) {
        return steps.TRAINING
      }
      if (tourType === TOUR_TYPE.PRIMARY_ONBOARDING) {
        return steps.PRIMARY_ONBOARDING
      }
      return steps.ONBOARDING
    },
    [steps]
  )
  return getTourSteps
}
